import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable jsdoc/require-returns */
/* eslint-disable @typescript-eslint/naming-convention */
import { forwardRef, useState, useEffect, useCallback, useContext, useMemo } from "react";
import { ColorPicker as FluentColorPicker, ColorSlider, ColorArea, AlphaSlider, makeStyles, tokens, Body1Strong, ColorSwatch, Body1, } from "@fluentui/react-components";
import { Color3, Color4 } from "core/Maths/math.color";
import { SpinButton } from "./spinButton";
import { TextInput } from "./textInput";
import { NumberDropdown } from "./dropdown";
import { ValidateColorHex } from "./utils";
import { Link } from "./link";
import { ToolContext } from "../hoc/fluentToolWrapper";
import { Popover } from "./popover";
const useColorPickerStyles = makeStyles({
    container: {
        width: "350px",
        display: "flex", // becomes a flexbox
        flexDirection: "column", // with children in a column
        alignItems: "center", // centers children horizontally
        justifyContent: "center", // centers children vertically (if height is set)
        gap: tokens.spacingVerticalM,
        overflow: "visible",
    },
    row: {
        flex: 1, // is a row in the container's flex column
        display: "flex", // becomes its own flexbox
        flexDirection: "row", // with children in a row
        gap: tokens.spacingHorizontalXL,
        alignItems: "center", // align items vertically
        width: "100%",
    },
    colorPicker: {
        flex: 1,
        width: "350px",
        height: "350px",
    },
    previewColor: {
        width: "60px",
        height: "60px",
        borderRadius: tokens.borderRadiusMedium, // 4px?
        border: `${tokens.spacingVerticalXXS} solid ${tokens.colorNeutralShadowKeyLighter}`,
        "@media (forced-colors: active)": {
            forcedColorAdjust: "none", // ensures elmement maintains color in high constrast mode
        },
    },
    inputRow: {
        display: "flex",
        flexDirection: "row",
        flex: 1, // grow and fill available space
        justifyContent: "center",
        gap: "10px",
        width: "100%",
    },
    inputField: {
        flex: 1, // grow and fill available space
        width: "auto",
        minWidth: 0,
        gap: tokens.spacingVerticalSNudge, // 6px
    },
    trigger: {
        display: "flex",
        alignItems: "center",
    },
});
export const ColorPickerPopup = forwardRef((props, ref) => {
    ColorPickerPopup.displayName = "ColorPickerPopup";
    const { value, onChange, isLinearMode, ...rest } = props;
    const classes = useColorPickerStyles();
    const [color, setColor] = useState(value);
    const [isLinear, setIsLinear] = useState(isLinearMode ?? false);
    const [isFloat, setFloat] = useState(false);
    const { size } = useContext(ToolContext);
    useEffect(() => {
        setColor(value); // Ensures the trigger color updates when props.value changes
    }, [value]);
    const isPropertyLinear = isLinearMode ?? false;
    /** Color in gamma space — used for visual elements (picker, preview, trigger) */
    const gammaColor = useMemo(() => (isPropertyLinear ? color.toGammaSpace() : color), [color, isPropertyLinear]);
    /** Color in the user-selected display space — used for numeric inputs (RGB, HSV, Hex) */
    const displayColor = useMemo(() => {
        if (isLinear === isPropertyLinear) {
            return color;
        }
        return isLinear ? color.toLinearSpace() : color.toGammaSpace();
    }, [color, isLinear, isPropertyLinear]);
    const handleColorPickerChange = (_, data) => {
        // The visual picker always operates in gamma space, convert back to property space
        let gammaResult = Color3.FromHSV(data.color.h, data.color.s, data.color.v);
        if (value instanceof Color4) {
            gammaResult = Color4.FromColor3(gammaResult, data.color.a ?? 1);
        }
        handleChange(isPropertyLinear ? gammaResult.toLinearSpace() : gammaResult);
    };
    const handleChange = (newColor) => {
        setColor(newColor);
        onChange(newColor); // Ensures the parent is notified when color changes from within colorPicker
    };
    const handleDisplayChange = (newDisplayColor) => {
        const propertyColor = isLinear === isPropertyLinear ? newDisplayColor : isLinear ? newDisplayColor.toGammaSpace() : newDisplayColor.toLinearSpace();
        handleChange(propertyColor);
    };
    return (_jsx(Popover, { trigger: _jsx(ColorSwatch, { className: classes.trigger, ref: ref, ...rest, borderColor: tokens.colorNeutralShadowKeyDarker, size: size === "small" ? "extra-small" : "small", shape: "rounded", color: gammaColor.toHexString(), value: gammaColor.toHexString().slice(1) }), children: _jsxs("div", { className: classes.container, children: [_jsxs(FluentColorPicker, { className: classes.colorPicker, color: rgbaToHsv(gammaColor), onColorChange: handleColorPickerChange, children: [_jsx(ColorArea, { inputX: { "aria-label": "Saturation" }, inputY: { "aria-label": "Brightness" } }), _jsx(ColorSlider, { "aria-label": "Hue" }), color instanceof Color4 && _jsx(AlphaSlider, { "aria-label": "Alpha" })] }), _jsxs("div", { className: classes.row, children: [_jsx("div", { className: classes.previewColor, style: { backgroundColor: gammaColor.toHexString() } }), _jsx(NumberDropdown, { className: classes.inputField, infoLabel: {
                                label: "Color Space",
                                info: (_jsxs(Body1, { children: ["Choose which color space to display numeric values in. This property stores its color in", " ", _jsx(Body1Strong, { children: isPropertyLinear ? "linear" : "gamma" }), " space. The visual picker always shows gamma (screen-accurate) colors."] })),
                            }, options: [
                                { label: "Gamma", value: 0 },
                                { label: "Linear", value: 1 },
                            ], value: isLinear ? 1 : 0, onChange: (val) => setIsLinear(val === 1) }), _jsx(NumberDropdown, { className: classes.inputField, infoLabel: {
                                label: "Data Type",
                                info: (_jsxs(Body1, { children: [_jsx(Body1Strong, { children: "Int" }), " displays RGB channels as integers in the 0\u2013255 range. ", _jsx(Body1Strong, { children: "Float" }), " displays them as decimals in the 0\u20131 range. This is display-only and does not affect the stored color."] })),
                            }, options: [
                                { label: "Int", value: 0 },
                                { label: "Float", value: 1 },
                            ], value: isFloat ? 1 : 0, onChange: (val) => setFloat(val === 1) })] }), _jsxs("div", { className: classes.inputRow, children: [_jsx(InputRgbField, { title: "Red", value: displayColor, rgbKey: "r", isFloat: isFloat, onChange: handleDisplayChange }), _jsx(InputRgbField, { title: "Green", value: displayColor, rgbKey: "g", isFloat: isFloat, onChange: handleDisplayChange }), _jsx(InputRgbField, { title: "Blue", value: displayColor, rgbKey: "b", isFloat: isFloat, onChange: handleDisplayChange }), _jsx(InputAlphaField, { color: color, onChange: handleChange })] }), _jsxs("div", { className: classes.inputRow, children: [_jsx(InputHsvField, { title: "Hue", value: displayColor, hsvKey: "h", isFloat: isFloat, onChange: handleDisplayChange }), _jsx(InputHsvField, { title: "Saturation", value: displayColor, hsvKey: "s", isFloat: isFloat, onChange: handleDisplayChange }), _jsx(InputHsvField, { title: "Value", value: displayColor, hsvKey: "v", isFloat: isFloat, onChange: handleDisplayChange })] }), _jsx("div", { className: classes.inputRow, children: _jsx(InputHexField, { title: "Hexadecimal", value: displayColor, isLinear: isLinear, isPropertyLinear: isPropertyLinear, onChange: handleDisplayChange }) })] }) }));
});
/**
 * Converts a hex string to the same Color type as the original.
 * Supports "#RGB", "#RRGGBB", and "#RRGGBBAA" formats.
 * For Color4, honors alpha from "#RRGGBBAA" input or preserves the original alpha otherwise.
 * @param hex - The hex string to convert, in one of the supported formats.
 * @param original - The original color, used to determine whether to return a Color3 or Color4 and to preserve alpha if not specified in hex.
 * @returns A new Color3 or Color4 instance representing the hex color
 */
function colorFromHex(hex, original) {
    const digits = hex.startsWith("#") ? hex.slice(1) : hex;
    // Normalize short hex (RGB => RRGGBB)
    if (digits.length === 3) {
        hex = `#${digits[0]}${digits[0]}${digits[1]}${digits[1]}${digits[2]}${digits[2]}`;
    }
    // 8 hex digits = RRGGBBAA — use Color4.FromHexString which natively handles this
    if (digits.length === 8) {
        if (original instanceof Color4) {
            return Color4.FromHexString(hex);
        }
        return Color3.FromHexString(hex.slice(0, 7));
    }
    // 6 hex digits = RRGGBB (or normalized from 3)
    if (original instanceof Color4) {
        return Color4.FromColor3(Color3.FromHexString(hex), original.a);
    }
    return Color3.FromHexString(hex);
}
/**
 * Component which displays the passed in color's HEX value in the currently selected color space.
 * When the hex color is changed by user, component calculates the new Color3/4 value and calls onChange.
 * @param props - The properties for the InputHexField component.
 * @returns
 */
export const InputHexField = (props) => {
    const classes = useColorPickerStyles();
    const { title, value, onChange, isLinear, isPropertyLinear } = props;
    const displayMismatchesProperty = (isLinear ?? false) !== (isPropertyLinear ?? false);
    const displaySpace = isLinear ? "linear" : "gamma";
    const propertySpace = isPropertyLinear ? "linear" : "gamma";
    const isColor4 = value instanceof Color4;
    const colorClass = isColor4 ? "Color4" : "Color3";
    return (_jsx(TextInput, { className: classes.inputField, value: value.toHexString(), validator: ValidateColorHex, validateOnlyOnBlur: true, onChange: (val) => onChange(colorFromHex(val, value)), infoLabel: title
            ? {
                label: title,
                info: (_jsxs(Body1, { children: ["This hex value is in ", _jsx(Body1Strong, { children: displaySpace }), " space", displayMismatchesProperty ? (_jsxs(Body1, { children: [", but the property stores its color in ", _jsx(Body1Strong, { children: propertySpace }), " space.", _jsx("br", {}), _jsx("br", {}), "The color picker converts automatically, but if you copy this hex into code you will need to convert it:", _jsx("br", {}), _jsxs(Body1Strong, { children: [colorClass, ".FromHexString(\"", value.toHexString(), "\").", isLinear ? "toGammaSpace()" : "toLinearSpace()"] })] })) : (_jsxs(Body1, { children: [", which matches the property's stored color space.", _jsx("br", {}), _jsx("br", {}), "To copy this hex into code, use", _jsx("br", {}), _jsxs(Body1Strong, { children: [colorClass, ".FromHexString(\"", value.toHexString(), "\")"] })] })), _jsx("br", {}), _jsx("br", {}), _jsx(Link, { url: "https://doc.babylonjs.com/preparingArtForBabylon/controllingColorSpace/", value: "Read more in our docs!" })] })),
            }
            : undefined }));
};
const InputRgbField = (props) => {
    const { value, onChange, title, rgbKey, isFloat } = props;
    const classes = useColorPickerStyles();
    const handleChange = useCallback((val) => {
        const newColor = value.clone();
        newColor[rgbKey] = isFloat ? val : val / 255.0;
        onChange(newColor);
    }, [value, onChange, rgbKey, isFloat]);
    return (_jsx(SpinButton, { infoLabel: title ? { label: title } : undefined, className: classes.inputField, min: 0, max: isFloat ? 1 : 255, value: isFloat ? value[rgbKey] : Math.round(value[rgbKey] * 255), step: isFloat ? 0.01 : 1, forceInt: !isFloat, precision: isFloat ? 4 : undefined, onChange: handleChange }, `${rgbKey}-${isFloat ? "float" : "int"}`));
};
function rgbaToHsv(color) {
    const c = new Color3(color.r, color.g, color.b);
    const hsv = c.toHSV();
    return { h: hsv.r, s: hsv.g, v: hsv.b, a: color.a };
}
// Internal HSV ranges: H ∈ [0,360], S ∈ [0,1], V ∈ [0,1]
// Int mode display:   H → 0-360, S → 0-100, V → 0-100
// Float mode display: H → 0-1,   S → 0-1,   V → 0-1
function getHsvDisplayParams(hsvKey, isFloat) {
    if (isFloat) {
        // All channels displayed as 0-1
        const internalMax = hsvKey === "h" ? 360 : 1;
        return { max: 1, toDisplay: (v) => v / internalMax, toInternal: (v) => v * internalMax, step: 0.01, forceInt: false, precision: 4 };
    }
    // Int mode
    const scale = hsvKey === "h" ? 1 : 100;
    const max = hsvKey === "h" ? 360 : 100;
    return { max, toDisplay: (v) => Math.round(v * scale), toInternal: (v) => v / scale, step: 1, forceInt: true, precision: undefined };
}
/**
 * In the HSV (Hue, Saturation, Value) color model, Hue (H) ranges from 0 to 360 degrees, representing the color's position on the color wheel.
 * Saturation (S) ranges from 0 to 100%, indicating the intensity or purity of the color, with 0 being shades of gray and 100 being a fully saturated color.
 * Value (V) ranges from 0 to 100%, representing the brightness of the color, with 0 being black and 100 being the brightest.
 * @param props - The properties for the InputHsvField component.
 */
export const InputHsvField = (props) => {
    const { value, title, hsvKey, isFloat, onChange } = props;
    const classes = useColorPickerStyles();
    const { max, toDisplay, toInternal, step, forceInt, precision } = getHsvDisplayParams(hsvKey, isFloat);
    const handleChange = useCallback((val) => {
        const hsv = rgbaToHsv(value);
        hsv[hsvKey] = toInternal(val);
        let newColor = Color3.FromHSV(hsv.h, hsv.s, hsv.v);
        if (value instanceof Color4) {
            newColor = Color4.FromColor3(newColor, value.a ?? 1);
        }
        props.onChange(newColor);
    }, [value, onChange, hsvKey, toInternal]);
    return (_jsx(SpinButton, { infoLabel: title ? { label: title } : undefined, className: classes.inputField, min: 0, max: max, value: toDisplay(rgbaToHsv(value)[hsvKey]), step: step, forceInt: forceInt, precision: precision, onChange: handleChange }, `${hsvKey}-${isFloat ? "float" : "int"}`));
};
/**
 * Displays the alpha value of a color, either in the disabled state (if color is Color3) or as a spin button (if color is Color4).
 * @param props
 * @returns
 */
const InputAlphaField = (props) => {
    const classes = useColorPickerStyles();
    const { color, onChange } = props;
    const handleChange = useCallback((value) => {
        if (Number.isNaN(value) || value < 0 || value > 1) {
            return;
        }
        if (color instanceof Color4) {
            const newColor = color.clone();
            newColor.a = value;
            onChange(newColor);
        }
        else {
            onChange(Color4.FromColor3(color, value));
        }
    }, [color, onChange]);
    return (_jsx(SpinButton, { disabled: color instanceof Color3, min: 0, max: 1, className: classes.inputField, value: color instanceof Color3 ? 1 : color.a, step: 0.01, onChange: handleChange, infoLabel: {
            label: "Alpha",
            info: color instanceof Color3 ? (_jsx(Body1, { children: "Because this color picker is representing a Color3, we do not permit modifying alpha from the color picker. You can however modify the property's alpha property directly, either in code via property.alpha OR via inspector's transparency section." })) : undefined,
        } }));
};
//# sourceMappingURL=colorPicker.js.map